import { Component } from '@angular/core';

import { ThemeService } from './theme/theme.service';
import { Router, RouterOutlet } from '@angular/router';
import { NavBarComponent } from './shared/components/nav-bar/nav-bar.component';
import { CommonModule } from '@angular/common';
import { AuthService } from '@auth0/auth0-angular';
import { switchMap, startWith, catchError, of, shareReplay, map, combineLatest, BehaviorSubject, Observable } from 'rxjs';
import { AccessRequestsService } from './api/access-requests.service';
import { AdminAccessRequestsService } from './api/admin-access-requests.service';
import { MeResponse } from './api/api.types';
import { MeService } from './api/me.service';
import { MainModel } from './models/main';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NavBarComponent, CommonModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  title = 'angular-web';
  currentYear = new Date().getFullYear();
  private readonly refresh$ = new BehaviorSubject<void>(undefined);
  vm$: Observable<MainModel>;

  constructor(
    private readonly theme: ThemeService,
    private readonly auth: AuthService,
    private readonly meService: MeService,
    private readonly adminAccessRequests: AdminAccessRequestsService
  ) {

    theme.init();
    const isAuthenticated$ = this.auth.isAuthenticated$;

    const me$ = this.refresh$.pipe(
      switchMap(() => this.meService.getMe(true)),
      startWith(null as MeResponse | null),
      catchError(() => of(null)),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    const pendingRequests$ = me$.pipe(
      switchMap((me) => {
        if (me?.user.role !== 'global_admin') {
          return of(null as number | null);
        }
        return this.adminAccessRequests.list().pipe(
          map((res) => res.users.filter((user) => user.approvalStatus !== 'approved').length),
          catchError(() => of(null))
        );
      }),
      startWith(null as number | null)
    );

    this.vm$ = combineLatest([isAuthenticated$, me$, pendingRequests$, this.theme.mode$, this.auth.user$]).pipe(
      map(([isAuthenticated, me, pendingRequests, themeMode, user]) => {
        if (!isAuthenticated) {
          return {
            isAuthenticated: false,
            canAccessWorkspace: false,
            showAccessNote: false,
            primaryLabel: 'Request Access',
            primaryDisabled: false,
            canRequestAccess: false,
            pendingRequests,
            themeMode,
            avatarInitial: '?',
            avatarLabel: 'Account',
          };
        }

        const canAccessWorkspace =
          me?.user.role === 'global_admin' || (me?.user.role === 'gm' && me?.user.isApproved);

        const isApprovedOrAdmin = !!canAccessWorkspace;
        const hasRequested = !!me?.user.accessRequestedAt;
        const canRequestAccess = !isApprovedOrAdmin && !hasRequested;
        const avatarLabel = (me?.user.email ?? me?.user.name ?? 'Account').trim();
        const avatarInitial = avatarLabel.charAt(0).toUpperCase() || '?';

        return {
          isAuthenticated: true,
          canAccessWorkspace: isApprovedOrAdmin,
          showAccessNote: !isApprovedOrAdmin,
          primaryLabel: isApprovedOrAdmin
            ? 'Continue to Workspace'
            : canRequestAccess
              ? 'Request Access'
              : 'Pending Approval',
          primaryDisabled: !isApprovedOrAdmin && !canRequestAccess,
          canRequestAccess,
          pendingRequests,
          themeMode,
          avatarInitial,
          avatarLabel,
          avatarPictureUri: user?.picture,
        };
      })
    );
  }
}
